import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Shared, persisted UI prefs for the vendo device listing. Both the
// Devices tab (/wifi-devices) and the Dashboard (/wifi-dashboard)
// consume this store so changing the sort/filter/view in one surface
// shows up immediately in the other and survives reloads.
//
// `expanded` (which card has its sub-vendo stack open) is intentionally
// NOT in here — that's per-surface UI state, not a preference. Each
// page keeps its own expanded Set locally.

export type DeviceViewMode = 'card' | 'list';
export type DeviceSort = 'newest' | 'status' | 'active' | 'revenue';
export type DeviceOwnershipFilter = 'owned' | 'sold' | 'all';
/** The composite pill on the card — NodeMCU × router, not device.status. */
export type DeviceHealthFilter = 'all' | 'online' | 'degraded' | 'offline' | 'online-or-degraded';
/** The BOARD, named the way Ray names it — never "Lite". 'all', 'mikrotik',
 *  or a device-catalog id (`ruijie-rg-ew1200g-pro-v1.1`, `comfast-cf-ew71-v2`,
 *  `x86-64`, …) so every board in the catalog is a filter option. The old
 *  values 'ruijie' / 'orangepi' may still sit in localStorage; the matcher
 *  maps them. */
export type DeviceBoardFilter = string;

interface DeviceListPrefsState {
   viewMode: DeviceViewMode;
   sortBy: DeviceSort;
   ownershipFilter: DeviceOwnershipFilter;
   healthFilter: DeviceHealthFilter;
   boardFilter: DeviceBoardFilter;
   showAllSubs: boolean;
   /** The search box over the device list. NOT persisted — a search is a
    *  question for now, and a reload that still hides most of the fleet
    *  behind yesterday's search would read as devices gone missing. */
   search: string;
   setViewMode: (m: DeviceViewMode) => void;
   setSearch: (q: string) => void;
   setSortBy: (s: DeviceSort) => void;
   setOwnershipFilter: (f: DeviceOwnershipFilter) => void;
   setHealthFilter: (f: DeviceHealthFilter) => void;
   setBoardFilter: (f: DeviceBoardFilter) => void;
   setShowAllSubs: (v: boolean) => void;
}

const useDeviceListPrefsStore = create<DeviceListPrefsState>()(
   persist(
      (set) => ({
         viewMode: 'card',
         sortBy: 'newest',
         ownershipFilter: 'owned',
         healthFilter: 'all',
         boardFilter: 'all',
         showAllSubs: false,
         search: '',
         setViewMode: (viewMode) => set({ viewMode }),
         setSearch: (search) => set({ search }),
         setSortBy: (sortBy) => set({ sortBy }),
         setOwnershipFilter: (ownershipFilter) => set({ ownershipFilter }),
         setHealthFilter: (healthFilter) => set({ healthFilter }),
         setBoardFilter: (boardFilter) => set({ boardFilter }),
         setShowAllSubs: (showAllSubs) => set({ showAllSubs }),
      }),
      {
         name: 'smartfi-device-list-prefs',
         storage: createJSONStorage(() => localStorage),
         partialize: (s) => ({
            viewMode: s.viewMode,
            sortBy: s.sortBy,
            ownershipFilter: s.ownershipFilter,
            healthFilter: s.healthFilter,
            boardFilter: s.boardFilter,
            showAllSubs: s.showAllSubs,
         }),
      }
   )
);

export default useDeviceListPrefsStore;
